import type { UnitInfoDataInterface } from 'store/unitInfoData';
import type { NodeSuggestion } from 'api/suggestions/suggestionsApi';
import { isUnitType, UnitNames, UnitTypes } from 'unitData/types';
import { getUnitInfoData } from 'util/getUnitInfoData';
import {
  CapaConstCalc_P80method,
  CHCrusherGetMaxCSS,
  maxFeedF100,
  maxFeedF90,
} from 'unitData/Crushers/CrusherConeCH/CrusherConeCHInfo';
import { getNominalCapacity } from 'unitData/Crushers/CrusherJawCJ/CrusherJawCJInfo';
import type { GetUnitImageOptions } from 'util/getUnitImage';
import { getUnitImage } from 'util/getUnitImage';

export type SmartSelectRecommendationCandidate = {
  unitType: UnitTypes;
  modelName?: string;
  concave?: string;
  mantle?: string;
  unitThrow?: number;
  variantId?: number;
  recommendationBasis: string;
  imageOptions?: GetUnitImageOptions;
};

export type SmartSelectRecommendedOption = {
  unitType: UnitTypes;
  title: string;
  equipmentType: string;
  imageSource: string | null;
  modelName?: string;
  concave?: string;
  mantle?: string;
  unitThrow?: number;
  variantId?: number;
  recommendationBasis: string;
  specLines: string[];
};

export type SmartSelectUnitInfoData = Pick<
  UnitInfoDataInterface,
  UnitTypes.CONE_CRUSHER_CH | UnitTypes.JAW_CRUSHER_CJ
>;

export const SMART_SELECT_RECOMMENDATION_CANDIDATES: SmartSelectRecommendationCandidate[] =
  [
    {
      unitType: UnitTypes.CONE_CRUSHER_CH,
      modelName: 'CH420',
      concave: 'C',
      mantle: 'FFB',
      unitThrow: 18,
      recommendationBasis: 'Frequently used',
    },
    {
      unitType: UnitTypes.CONE_CRUSHER_CH,
      modelName: 'CH420',
      concave: 'M',
      mantle: 'B',
      unitThrow: 18,
      recommendationBasis: 'Compatible with output',
    },
    {
      unitType: UnitTypes.JAW_CRUSHER_CJ,
      modelName: 'CJ815',
      recommendationBasis: 'High feed opening',
    },
  ];

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const compact = <T>(items: Array<T | null | undefined | false>): T[] =>
  items.filter((item): item is T => Boolean(item));

const formatMillimeters = (value: number) => `${value} mm`;

const getEquipmentType = (unitType: UnitTypes) => {
  const unitName = UnitNames.find((candidate) => candidate.type === unitType);

  return unitName?.name2 ?? unitName?.name ?? unitType;
};

const formatProbability = (probability: number) => {
  const normalizedProbability =
    probability > 1 ? probability / 100 : probability;

  return `${Math.round(normalizedProbability * 100)}% of approved flowsheets`;
};

type DefaultModelCapability = {
  modelName?: string;
  maxFeedSize?: number;
};

const MAX_FEED_SIZE_KEYS = [
  'maxFeed',
  'maxFeedSize',
  'maxFeedSizeF100',
  'f100maxFeedSize',
] as const;

const getMaxFeedSize = (modelData: Record<string, unknown>) => {
  for (const key of MAX_FEED_SIZE_KEYS) {
    const value = modelData[key];

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }

  return undefined;
};

const getUnitCatalogData = (
  unitType: UnitTypes,
  unitInfoData: UnitInfoDataInterface,
) =>
  unitInfoData[unitType as keyof UnitInfoDataInterface] ??
  getUnitInfoData(unitType, unitInfoData);

const getKnownModelCapabilities = (
  unitType: UnitTypes,
  unitInfoData: UnitInfoDataInterface,
): DefaultModelCapability[] => {
  try {
    const unitData = getUnitCatalogData(unitType, unitInfoData);

    if (typeof unitData !== 'object' || unitData === null) {
      return [];
    }

    return Object.entries(unitData as Record<string, unknown>).flatMap(
      ([key, value]) => {
        if (typeof value !== 'object' || value === null) {
          return [];
        }

        const modelData = value as Record<string, unknown>;
        const maxFeedSize = getMaxFeedSize(modelData);

        if (maxFeedSize === undefined) {
          return [];
        }

        return [
          {
            modelName:
              typeof modelData.modelName === 'string'
                ? modelData.modelName
                : key,
            maxFeedSize,
          },
        ];
      },
    );
  } catch {
    return [];
  }
};

export const mapNodeSuggestionsToCandidates = (
  suggestions: NodeSuggestion[],
): SmartSelectRecommendationCandidate[] =>
  suggestions.flatMap((suggestion) => {
    const unitType = suggestion.unitType ?? suggestion.type;

    if (!isUnitType(unitType)) {
      return [];
    }

    return [
      {
        unitType,
        modelName: suggestion.modelName,
        concave: suggestion.concave,
        mantle: suggestion.mantle,
        unitThrow: suggestion.unitThrow,
        variantId: suggestion.variantId,
        recommendationBasis: formatProbability(suggestion.probability),
        imageOptions:
          typeof suggestion.variantId === 'number'
            ? { variantId: suggestion.variantId }
            : undefined,
      },
    ];
  });

const getConeChMaxFeedLine = (
  candidate: SmartSelectRecommendationCandidate,
  unitInfoData: SmartSelectUnitInfoData,
) => {
  const { modelName, mantle, concave } = candidate;

  if (!modelName || !mantle || !concave) {
    return null;
  }

  const coneData = unitInfoData[UnitTypes.CONE_CRUSHER_CH];

  if (!coneData?.[modelName]?.concaves?.[concave]?.mantles?.[mantle]) {
    return null;
  }

  try {
    const f100 = maxFeedF100(modelName, mantle, concave, false, coneData);
    const f90 = maxFeedF90(modelName, mantle, concave, false, coneData);

    if (!isFiniteNumber(f100) || !isFiniteNumber(f90)) {
      return null;
    }

    return `Max feed size: ${f100} / ${f90} mm`;
  } catch {
    return null;
  }
};

const getConeChSpecLines = (
  candidate: SmartSelectRecommendationCandidate,
  unitInfoData: SmartSelectUnitInfoData,
) => {
  const chamber = compact([
    candidate.concave,
    candidate.mantle,
    candidate.unitThrow ? `${candidate.unitThrow} mm throw` : null,
  ]).join(', ');

  return compact([
    getConeChMaxFeedLine(candidate, unitInfoData),
    chamber ? `Chamber: ${chamber}` : null,
  ]);
};

const getCandidateMaxFeedSize = (
  candidate: SmartSelectRecommendationCandidate,
  unitInfoData: SmartSelectUnitInfoData,
) => {
  if (
    candidate.unitType === UnitTypes.CONE_CRUSHER_CH &&
    candidate.modelName &&
    candidate.mantle &&
    candidate.concave
  ) {
    const coneData = unitInfoData[UnitTypes.CONE_CRUSHER_CH];

    try {
      const maxFeedSize = maxFeedF100(
        candidate.modelName,
        candidate.mantle,
        candidate.concave,
        false,
        coneData,
      );
      return isFiniteNumber(maxFeedSize) ? maxFeedSize : undefined;
    } catch {
      return undefined;
    }
  }

  if (candidate.unitType === UnitTypes.JAW_CRUSHER_CJ && candidate.modelName) {
    const maxFeedSize =
      unitInfoData[UnitTypes.JAW_CRUSHER_CJ]?.[candidate.modelName]?.maxFeed;
    return isFiniteNumber(maxFeedSize) ? maxFeedSize : undefined;
  }

  if (candidate.modelName) {
    return getKnownModelCapabilities(
      candidate.unitType,
      unitInfoData as UnitInfoDataInterface,
    ).find(({ modelName }) => modelName === candidate.modelName)?.maxFeedSize;
  }

  return undefined;
};

const getCandidateMaxCapacity = (
  candidate: SmartSelectRecommendationCandidate,
  unitInfoData: SmartSelectUnitInfoData,
) => {
  if (candidate.unitType === UnitTypes.CONE_CRUSHER_CH) {
    const { modelName, concave, mantle, unitThrow } = candidate;
    if (!modelName || !concave || !mantle || !isFiniteNumber(unitThrow)) {
      return undefined;
    }

    try {
      const coneData = unitInfoData[UnitTypes.CONE_CRUSHER_CH];
      const maxCss = CHCrusherGetMaxCSS(
        modelName,
        mantle,
        concave,
        unitThrow,
        coneData,
      );
      if (!isFiniteNumber(maxCss) || maxCss <= 0) {
        return undefined;
      }
      const capacity = CapaConstCalc_P80method(
        modelName,
        mantle,
        concave,
        maxCss,
        unitThrow,
        coneData,
      );
      return isFiniteNumber(capacity) && capacity > 0 ? capacity : undefined;
    } catch {
      return undefined;
    }
  }

  if (candidate.unitType === UnitTypes.JAW_CRUSHER_CJ && candidate.modelName) {
    const jawData = unitInfoData[UnitTypes.JAW_CRUSHER_CJ];
    const upperCss = jawData?.[candidate.modelName]?.upperCSS;
    if (!isFiniteNumber(upperCss) || upperCss <= 0) {
      return undefined;
    }

    const capacity = getNominalCapacity(candidate.modelName, upperCss, jawData);
    return isFiniteNumber(capacity) && capacity > 0 ? capacity : undefined;
  }

  if (candidate.modelName) {
    try {
      const unitData = getUnitCatalogData(
        candidate.unitType,
        unitInfoData as UnitInfoDataInterface,
      );
      const modelData = (unitData as Record<string, unknown>)[
        candidate.modelName
      ] as Record<string, unknown> | undefined;
      const capacity = modelData?.nominalCapacity;
      return isFiniteNumber(capacity) && capacity > 0 ? capacity : undefined;
    } catch {
      return undefined;
    }
  }

  return undefined;
};

export const isSmartSelectCandidateCompatible = (
  candidate: SmartSelectRecommendationCandidate,
  particleSize: number | undefined,
  unitInfoData: SmartSelectUnitInfoData,
  capacity?: number,
) => {
  if (isFiniteNumber(particleSize)) {
    const maxFeedSize = getCandidateMaxFeedSize(candidate, unitInfoData);
    if (maxFeedSize !== undefined && maxFeedSize < particleSize) {
      return false;
    }
  }

  if (isFiniteNumber(capacity)) {
    const maxCapacity = getCandidateMaxCapacity(candidate, unitInfoData);
    if (maxCapacity !== undefined && maxCapacity < capacity) {
      return false;
    }
  }

  return true;
};

const getJawSpecLines = (
  candidate: SmartSelectRecommendationCandidate,
  unitInfoData: SmartSelectUnitInfoData,
) => {
  if (!candidate.modelName) {
    return [];
  }

  const jawData = unitInfoData[UnitTypes.JAW_CRUSHER_CJ]?.[candidate.modelName];

  if (!jawData) {
    return [];
  }

  return compact([
    isFiniteNumber(jawData.maxFeed)
      ? `Max feed size: ${formatMillimeters(jawData.maxFeed)}`
      : null,
    isFiniteNumber(jawData.feedOpenWidth) &&
    isFiniteNumber(jawData.feedOpenHeight)
      ? `Feed opening: ${jawData.feedOpenWidth} x ${jawData.feedOpenHeight} mm`
      : null,
    isFiniteNumber(jawData.lowerCSS) && isFiniteNumber(jawData.upperCSS)
      ? `CSS range: ${jawData.lowerCSS}-${jawData.upperCSS} mm`
      : null,
  ]);
};

const getSpecLines = (
  candidate: SmartSelectRecommendationCandidate,
  unitInfoData: SmartSelectUnitInfoData,
) => {
  switch (candidate.unitType) {
    case UnitTypes.CONE_CRUSHER_CH:
      return getConeChSpecLines(candidate, unitInfoData);
    case UnitTypes.JAW_CRUSHER_CJ:
      return getJawSpecLines(candidate, unitInfoData);
    default:
      return [];
  }
};

export const getSmartSelectRecommendedOptions = (
  unitInfoData: SmartSelectUnitInfoData,
  candidates = SMART_SELECT_RECOMMENDATION_CANDIDATES,
  particleSize?: number,
  capacity?: number,
): SmartSelectRecommendedOption[] =>
  candidates
    .filter((candidate) =>
      isSmartSelectCandidateCompatible(
        candidate,
        particleSize,
        unitInfoData,
        capacity,
      ),
    )
    .map((candidate) => {
      const imageOptions: GetUnitImageOptions = {
        ...candidate.imageOptions,
        modelName: candidate.modelName ?? candidate.imageOptions?.modelName,
        variantId: candidate.variantId ?? candidate.imageOptions?.variantId,
      };

      return {
        unitType: candidate.unitType,
        title: candidate.modelName ?? getEquipmentType(candidate.unitType),
        equipmentType: getEquipmentType(candidate.unitType),
        imageSource: getUnitImage(candidate.unitType, imageOptions),
        modelName: candidate.modelName,
        concave: candidate.concave,
        mantle: candidate.mantle,
        unitThrow: candidate.unitThrow,
        variantId: imageOptions.variantId,
        recommendationBasis: candidate.recommendationBasis,
        specLines: getSpecLines(candidate, unitInfoData),
      };
    });
